import { useCallback, useEffect, useRef, useState } from 'react'

import {
  getDrama,
  requestPlayback,
  type DramaDetail,
  type Episode,
  type Playback,
} from '../../api/drama'
import { ApiError, GateError, type GateDenied } from '../../api/request'
import { errorText, t } from '../../i18n'
import type { StartTarget } from '../../lib/startParam'
import { EpisodeBand, EpisodeSheet, SHEET_RATIO } from './components/EpisodeBand'
import { EpisodeFeed } from './components/EpisodeFeed'
import { LockedSlot, PosterSlot } from './components/EpisodeSlot'
import { GateSheet } from './components/GateSheet'
import { Player } from './components/Player'
import { BoostBand } from './components/PlayerControls'
import { useSession } from './hooks/useSession'
import { useTelemetry } from './hooks/useTelemetry'

interface Props {
  target: StartTarget
}

/** 当前集起播多久之后去取下一集的地址。等一下是为了别让连着快滑的人把每一集都请求一遍。 */
const PREFETCH_DELAY_MS = 1500
/** 预取的地址快过期就当没有。签名过期的地址拿去播就是整集 403。 */
const EXPIRY_GUARD_MS = 10_000
/** 选集面板打开时，缩小的视频底边离面板上沿留多少（和 App 一样 48）。 */
const SHRINK_GAP = 48

/**
 * 唯一的页面：一部剧一页，没有聚合页也没有搜索。
 *
 * 版式和 App 的剧集播放页一致（照红果）：上面是竖向的剧集流，一集一屏、跟手上下滑、播完自动下一集；
 * 下面一条 56 的黑带放选集条，点开选集面板，面板打开时视频缩到上方照常播。
 * **页面本身不滚动**——竖屏视频本来就占满一屏，下面再堆内容，播放控件就会被挤到屏幕外。
 *
 * 这里承担全部编排 —— 建会话、拉数据、换集、申请播放、预取下一集、
 * 处理门禁、驱动埋点；各个子组件只负责渲染自己那一块。
 */
export function WatchPage({ target }: Props) {
  const session = useSession(target.source)
  const telemetry = useTelemetry(target.source)

  /**
   * 片源清一色竖版，没有横屏播放体验——红果也只给横版内容配横屏/全屏入口。
   * 尽力锁竖屏，但不强求：这个 API 通常要求页面已经处于 Fullscreen 态才生效，
   * 在 Telegram 的 WebView 里大概率是空操作。锁不住也无妨——我们没有为横屏
   * 单独设计界面，转过去画面照旧正常布局在更宽的视口里，不会露出半成品 UI。
   */
  useEffect(() => {
    // `lock` 不在标准 ScreenOrientation 类型里（支持面还很窄，各家实现不一致）
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (o: string) => Promise<void>
    }
    orientation.lock?.('portrait').catch(() => {})
  }, [])

  const [drama, setDrama] = useState<DramaDetail | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [index, setIndex] = useState(0)
  /**
   * 这一次换集要不要翻页动画。
   *
   * 和 index 一起改，React 会把两个更新批到同一次渲染，所以 feed 渲染那一刻
   * 就知道该不该动。放在 feed 内部用 effect 事后判断是不行的——
   * 那时 DOM 已经跳到新位置了，再开过渡也没有可动的东西。
   */
  const [animate, setAnimate] = useState(false)
  /**
   * 已经拿到的播放地址，按集号存。
   * 留着是为了「滑回上一集」和「预取下一集」都不用再请求一次——
   * 签名在有效期内可以重复用，过期的由 usable() 挡掉。
   */
  const [playbacks, setPlaybacks] = useState<Record<number, Playback>>({})
  const [denied, setDenied] = useState<GateDenied | null>(null)
  const [picking, setPicking] = useState(false)
  /** 选集面板打开时视频缩到的比例。打开那一刻按视频区实际高度算一次。 */
  const [shrink, setShrink] = useState(1)
  const videoAreaRef = useRef<HTMLDivElement>(null)
  /** 当前集在播（不是暂停）。选集面板里的音柱跟着它动或停。 */
  const [playing, setPlaying] = useState(false)
  // 这一集播不了，和「整页加载失败」是两回事：前者要留在这一屏里让人滑走或换一集，
  // 后者才该整页替换。混成一个的话，滑到一集没上片就把整个界面清空了。
  const [episodeError, setEpisodeError] = useState<string | null>(null)
  /** 长按视频两侧顶倍速期间为真，临时关掉 EpisodeFeed 的竖滑换集判定。 */
  const [gestureLocked, setGestureLocked] = useState(false)
  /** 清屏播放。放在页面而不是播放器里：播放器每集重建一次，清屏要跨集保持。 */
  const [clean, setClean] = useState(false)

  const episodes = drama?.episodes ?? []
  const current: Episode | undefined = episodes[index]
  const playback = current ? playbacks[current.id] : undefined

  // 会话状态定下来才拉数据：匿名与已登录看到的门禁结论不同，
  // 抢先拉一次会让用户看到闪烁的锁图标。
  const sessionSettled = session.status !== 'pending'

  useEffect(() => {
    if (!sessionSettled) return
    let alive = true
    setLoadError(null)

    getDrama(target.slug, target.source)
      .then((detail) => {
        if (!alive) return
        setDrama(detail)
        // 深链带了集号就直接定位到那一集，否则从第一集开始
        const at = target.episodeNumber
          ? detail.episodes.findIndex((e) => e.number === target.episodeNumber)
          : 0
        setIndex(at >= 0 ? at : 0)
      })
      .catch((err: unknown) => {
        if (!alive) return
        setLoadError(err instanceof ApiError ? errorText(err.code) : errorText('unknown'))
      })

    return () => {
      alive = false
    }
  }, [sessionSettled, target.slug, target.source, target.episodeNumber])

  /** 手上的地址还能用吗。签名一过期，剩下的切片全是 403。 */
  const usable = useCallback(
    (p: Playback | undefined) =>
      !!p && new Date(p.expiresAt).getTime() - Date.now() > EXPIRY_GUARD_MS,
    [],
  )

  const fetchPlayback = useCallback(
    async (episode: Episode): Promise<Playback | null> => {
      const granted = await requestPlayback(episode.id, target.source)
      setPlaybacks((prev) => ({ ...prev, [episode.id]: granted }))
      return granted
    },
    [target.source],
  )

  // 换到哪一集，就给哪一集申请播放。门禁要重新校验，签名地址也是一集一签。
  // 锁住的集不请求：那一屏本来就渲染成门禁卡片，连着滑过去只会打出一串必然 403 的请求。
  useEffect(() => {
    if (!current || current.locked) return
    telemetry.resetEpisode()
    setEpisodeError(null)
    if (usable(playbacks[current.id])) return

    let alive = true
    fetchPlayback(current).catch((err: unknown) => {
      if (!alive) return
      if (err instanceof GateError) setDenied(err.denied)
      else if (err instanceof ApiError) setEpisodeError(errorText(err.code))
      else setEpisodeError(errorText('unknown'))
    })
    return () => {
      alive = false
    }
    // playbacks 不进依赖：它一变这里就会重跑，而这里又在写它
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, current?.locked])

  // 预取下一集的地址。**只取地址，不取视频**：
  // 一个几百字节的 JSON 换掉切集时的一次往返，而预拉切片要烧用户的移动流量。
  useEffect(() => {
    const next = episodes[index + 1]
    if (!next || next.locked || usable(playbacks[next.id])) return
    const timer = window.setTimeout(() => {
      // 预取失败不报错：等真滑过去时还会再请求一次，那次才需要告诉用户
      fetchPlayback(next).catch(() => {})
    }, PREFETCH_DELAY_MS)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, episodes.length])

  /**
   * 换集的唯一入口：手滑、滚轮、播完自动下一集、分集抽屉，全走这里。
   *
   * 隔着一集的才翻页——自动下一集也要这段动画，**否则下一集接上来时毫无感知，
   * 人会以为是当前这集从头又播了一遍**。隔着好几集（抽屉里跳）就直接到位。
   */
  const goTo = useCallback(
    (next: number) => {
      if (next === index) return
      if (playback && current) {
        telemetry.push('episode_switch', playback.sessionId, current.id, 0)
      }
      setAnimate(Math.abs(next - index) === 1)
      setIndex(next)
    },
    [index, playback, current, telemetry],
  )

  const handleEnded = useCallback(() => {
    if (!current || !playback) return
    telemetry.push('play_end', playback.sessionId, current.id, 0)
    telemetry.flush()
    // 自动进入下一集，接住最容易流失的那一刻。走和手滑同一条路，所以也有那段翻页动画。
    if (index + 1 < episodes.length) goTo(index + 1)
  }, [current, playback, telemetry, index, episodes.length, goTo])

  /** 锁住的那一屏点了解锁：这时才去要真正的跳转链接（频道 / 机器人）。 */
  const unlock = useCallback(
    (episode: Episode) => {
      fetchPlayback(episode).catch((err: unknown) => {
        // 正常路径就是这里：服务端回 403，带着该去哪儿的链接
        if (err instanceof GateError) setDenied(err.denied)
      })
    },
    [fetchPlayback],
  )

  const openSheet = useCallback(() => {
    const area = videoAreaRef.current
    const top = window.innerHeight * (1 - SHEET_RATIO) - SHRINK_GAP
    setShrink(area && area.clientHeight > 0 ? Math.max(0.1, Math.min(1, top / area.clientHeight)) : 1)
    setPicking(true)
    // 「看过」是服务端按有效播放算的：先把攒着的埋点发掉，再刷新一次，刚看的这集才标得上
    telemetry
      .flush()
      .then(() => getDrama(target.slug, target.source))
      .then(setDrama)
      .catch(() => {})
  }, [telemetry, target.slug, target.source])
  const failCurrent = useCallback(() => setEpisodeError(errorText('no_media')), [])

  if (loadError) {
    return (
      <main style={styles.center}>
        <p>{loadError}</p>
        <button style={styles.retry} onClick={() => window.location.reload()}>
          {t.retry}
        </button>
      </main>
    )
  }

  if (!drama || !sessionSettled || episodes.length === 0) {
    return (
      <main style={styles.center}>
        <p>{drama && episodes.length === 0 ? errorText('no_media') : t.loading}</p>
      </main>
    )
  }

  return (
    <main style={styles.stage}>
      <div
        ref={videoAreaRef}
        style={{ ...styles.videoArea, transform: picking ? `scale(${shrink})` : 'none' }}
      >
        <EpisodeFeed
          episodes={episodes}
          index={index}
          animate={animate}
          onIndexChange={goTo}
          gestureLocked={gestureLocked}
        >
          {(episode, isCurrent) => {
            if (!isCurrent) {
              return <PosterSlot coverUrl={drama.coverUrl} number={episode.number} />
            }
            if (episode.locked) {
              return (
                <LockedSlot
                  coverUrl={drama.coverUrl}
                  number={episode.number}
                  gate={episode.gate}
                  onUnlock={() => unlock(episode)}
                />
              )
            }
            const granted = playbacks[episode.id]
            if (!granted || episodeError) {
              // 还在要地址，或者这一集没上片。封面照旧铺着，人可以直接滑走。
              return (
                <div style={styles.pending}>
                  <PosterSlot coverUrl={drama.coverUrl} number={episode.number} />
                  <p style={styles.pendingText}>{episodeError ?? t.loading}</p>
                </div>
              )
            }
            return (
              <Player
                key={episode.id}
                playback={granted}
                title={drama.titleKm}
                episodeNumber={episode.number}
                posterUrl={drama.coverUrl}
                onStart={() => telemetry.push('play_start', granted.sessionId, episode.id, 0)}
                onProgress={(pos, dur, h) =>
                  telemetry.onProgress(granted.sessionId, episode.id, pos, dur, h)
                }
                onEnded={handleEnded}
                onPlayingChange={setPlaying}
                chromeHidden={picking}
                onFailed={failCurrent}
                onBoostActiveChange={setGestureLocked}
                clean={clean}
                onCleanChange={setClean}
              />
            )
          }}
        </EpisodeFeed>
      </div>

      {/* 长按顶速期间选集条换成黑条（红果实机：黑条不压视频） */}
      {gestureLocked ? <BoostBand /> : <EpisodeBand drama={drama} onOpen={openSheet} />}

      {/* 面板不压暗后面，但上方空白要能点：点一下收起，也挡住视频上的手势 */}
      {picking && <div style={styles.dismiss} onClick={() => setPicking(false)} role="presentation" />}

      <EpisodeSheet
        open={picking}
        drama={drama}
        currentId={current?.id ?? null}
        playing={playing}
        onSelect={(episode) => {
          const at = episodes.findIndex((e) => e.id === episode.id)
          if (at >= 0) goTo(at)
          setPicking(false)
        }}
      />

      {denied && <GateSheet denied={denied} onClose={() => setDenied(null)} />}
    </main>
  )
}

const styles = {
  stage: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#000',
    overflow: 'hidden',
  },
  videoArea: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
    transformOrigin: 'top center',
    transition: 'transform .25s ease-in-out',
  },
  dismiss: { position: 'fixed', inset: 0, zIndex: 19 },
  center: {
    minHeight: '100dvh',
    display: 'grid',
    placeItems: 'center',
    gap: 14,
    padding: 24,
    textAlign: 'center',
  },
  retry: { padding: '10px 22px', background: 'var(--accent-gradient)', color: '#fff' },
  pending: { position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' },
  pendingText: {
    position: 'relative',
    margin: 0,
    marginTop: 34,
    color: 'var(--text-dim)',
    fontSize: 13,
  },
} satisfies Record<string, React.CSSProperties>
